import { createResource, type Accessor } from 'solid-js'
import { parse, safeParse } from 'valibot'
import { backendAxios } from '../../axios'
import type {
  IndexMapQuery,
  MapRowCollection,
} from '../../backend.d'
import {
  IndexMapQuerySchema,
} from '../../backend_schemas'
import { mapDisk } from '../../lib/core/disk/IndexedDBDisk'
import { hasIndexedDB } from '../../lib/core/Storage/IndexedDB/hasIndexedDB'
import { isOfflineOnly } from '../../lib/core/isOfflineOnly'
import { getDefaultStateMapDisk } from '../../lib/core/state/getDefaultStateMapDisk'
import { StateSchema } from '../../lib/core/state/State.type'
import { StateMapDisk } from '../../lib/core/state/state_map_disk.enum'

export interface ExtendedIndexMapQuery extends IndexMapQuery {
  disk?: StateMapDisk
}

async function fetchMapCollectionByQuery(inputParams: ExtendedIndexMapQuery): Promise<MapRowCollection> {
  const disk = inputParams.disk || getDefaultStateMapDisk()
  const result = safeParse(IndexMapQuerySchema, inputParams)

  if (result.success) {
    const params = result.output
    let collection: MapRowCollection = []

    if (disk === StateMapDisk.IndexedDB) {
      if (!hasIndexedDB()) {
        throw new Error('IndexedDB requested but not available')
      }
      collection = await mapDisk.list()
    } else if (disk === StateMapDisk.Cloud && !isOfflineOnly && import.meta.env.VITE_HONO_URL) {
      try {
        const response = await backendAxios.get(`/st1/map`, { params })
        collection = response.data as MapRowCollection
      } catch (e) {
        console.error('Failed to fetch maps from cloud', e)
      }
    } else {
      throw new Error('Invalid disk: ' + disk)
    }

    collection.forEach((row): void => {
      row.data = parse(StateSchema, row.data)
    })
    return collection
  }
  return []
}

export default function createMapCollectionResource(
  getInputQuery: Accessor<Partial<ExtendedIndexMapQuery>>
) {
  const resource = createResource(getInputQuery, fetchMapCollectionByQuery)
  return resource
}
